import { useQuery } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as services from '../../../../../components/FilterSection/services';
import { AsyncCheckPillProps } from '../../../../../components/Filters';
import { selectorPrecoAtacadoFiltersValue, UPDATE_PRECO_ATACADO_FILTERS_VALUE } from '../../../../../reducers/PrecoAtacado/filters/values';
import { checkIfFiltersValuesIsEmpty } from '../../../../../utils';

export type FilterObject<K extends string = string> = {
  dataKey: K;
} & AsyncCheckPillProps;

type FILTER_KEYS = 'productIds' | 'productFamilyIds' | 'storeIds' | 'storeType' | 'clusters' | 'productBrand' | 'supplier' | string;

type Queries = FilterObject<FILTER_KEYS>[];

const usePrecoAtacadoFilters = () => {
  const dispatch = useDispatch();

  const values = useSelector(selectorPrecoAtacadoFiltersValue);

  // Mudança de filtro não mexe na seleção, igual ao gerenciador de preços
  // (useFilterSection.onSelect). A seleção só é desfeita por "Limpar seleção"
  // ou depois de uma ação em massa.
  const set = useCallback(
    (key: FILTER_KEYS, value: string[]) => {
      dispatch(UPDATE_PRECO_ATACADO_FILTERS_VALUE({ key, value }));
    },
    [dispatch],
  );

  const handleClean = useCallback(
    (key: FILTER_KEYS) => {
      set(key, []);
    },
    [set],
  );

  const queries: Queries = React.useMemo(
    () => [
      {
        dataKey: 'productIds',
        placeholder: 'Produto',
        queryFn: services.listProducts,
        pasteValues: true,
        pasteQueryFn: query => services.listProducts(query, { isModal: true }),
        value: values.productIds,
        onSelect: value => set('productIds', value),
        onClean: () => handleClean('productIds'),
      },
      {
        dataKey: 'productFamilyIds',
        placeholder: 'Família',
        queryFn: services.listFamilies,
        value: values.productFamilyIds,
        onSelect: value => set('productFamilyIds', value),
        onClean: () => handleClean('productFamilyIds'),
      },
      {
        dataKey: 'storeIds',
        placeholder: 'Loja',
        queryFn: services.listStores,
        value: values.storeIds,
        selectAll: true,
        onSelect: value => set('storeIds', value),
        onClean: () => handleClean('storeIds'),
      },
      {
        dataKey: 'storeType',
        placeholder: 'Tipo de loja',
        queryFn: services.listStoreType,
        value: values.storeType,
        onSelect: value => set('storeType', value),
        onClean: () => handleClean('storeType'),
      },
      {
        dataKey: 'clusters',
        placeholder: 'Clusters',
        queryFn: services.listClusters,
        value: values.clusters,
        onSelect: value => set('clusters', value),
        onClean: () => handleClean('clusters'),
      },
      {
        dataKey: 'productBrand',
        placeholder: 'Marca',
        queryFn: services.listProductBrands,
        value: values.productBrand,
        onSelect: value => set('productBrand', value),
        onClean: () => handleClean('productBrand'),
      },
      {
        dataKey: 'supplier',
        placeholder: 'Fornecedor',
        queryFn: services.listSuppliers,
        value: values.supplier,
        onSelect: value => set('supplier', value),
        onClean: () => handleClean('supplier'),
      },
    ],
    [values, set, handleClean],
  );

  const { data: segments } = useQuery({
    queryKey: ['list-segments'],
    queryFn: services.listaSegmentos,
    initialData: [],
  });

  const segmentsQueries: Queries = React.useMemo(() => {
    return segments.map(segment => ({
      dataKey: segment.field,
      placeholder: segment.name,
      value: values[segment.field],
      queryFn: query => services.listSegment(query, segment.level),
      level: segment.level,
      onSelect: value => set(segment.field, value),
      onClean: () => handleClean(segment.field),
    }));
  }, [segments, values, set, handleClean]);

  const filtersWithSegments = React.useMemo(() => [...queries, ...segmentsQueries], [queries, segmentsQueries]);

  const memoValues = React.useMemo(() => values, [values]);

  const isEmpty = checkIfFiltersValuesIsEmpty(values);

  return {
    list: filtersWithSegments,
    values: memoValues,
    isEmpty,
  } as const;
};

export default usePrecoAtacadoFilters;
